import dayjs from "dayjs";
import { useState } from "react";
import { OrganizationAPIKeyData } from "@/types/organizations";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "@/redux";
import { createOrgAPIKey, getOrgAPIKey, removeOrgAPIKey } from "@/services/organizationService";

export const OrganizationApiKeys = ({ orgId }: { orgId: string }) => {
  const dispatch: AppDispatch = useDispatch()
  const { token } = useSelector((state: RootState) => state.user)
  const { organizations } = useSelector((state: RootState) => state.organization)
  const [copiedKeyId, setCopiedKeyId] = useState<string | null>(null);
  const [apiKeys, setApiKeys] = useState<OrganizationAPIKeyData[]>([])
  const org = organizations.find(o => o.id === orgId)

  const handleFetch = async () => {
    if (!token) return
    const apiKeysResponse = await dispatch(getOrgAPIKey(token, orgId))
    if (apiKeysResponse.data) {
      setApiKeys(apiKeysResponse.data)
    }
  }

  const handleCreate = async () => {
    if (!token) return
    const apiKeysResponse = await dispatch(createOrgAPIKey(token, orgId))
    if (apiKeysResponse.data) {
      setApiKeys([apiKeysResponse.data])
    }
  }

  const handleCopy = (apiKey: string, id: string) => {
    navigator.clipboard.writeText(apiKey);
    setCopiedKeyId(id);

    setTimeout(() => {
      setCopiedKeyId(null);
    }, 3000);
  };

  const handleDelete = async (apiKeyId: string) => {
    if (!token) return
    const resp = await dispatch(removeOrgAPIKey(token, orgId, apiKeyId))
    if (resp.data) {
      const apiKeysResponse = await dispatch(getOrgAPIKey(token, orgId))
      if (apiKeysResponse.data) {
        setApiKeys(apiKeysResponse.data)
      }
    }
  }

  const getApiKeyStatusClassName = (status: boolean): string => {
    const baseClass = 'absolute -top-3 right-0 text-xs px-2 py-0.5 rounded-full font-medium';

    const statusClass = status
      ? 'bg-success-foreground text-success'
      : 'bg-destructive-foreground text-destructive';

    return `${baseClass} ${statusClass}`;
  };

  return (
    <div className="text-primary pt-6">
      <div className="flex items-center mb-2">
        <h4 className="text-lg font-semibold text-left mr-4">API Keys</h4>
      </div>
      {apiKeys?.length === 0 ? (
        <p className="text-sm text-gray-500">
          For security reasons, API keys are not displayed by default.
          Click <span className="text-accent underline font-semibold cursor-pointer" onClick={() => handleFetch()}>here</span>{" "}
          to load and view keys.
        </p>
      ) : null}
      {apiKeys?.length > 0 && apiKeys?.filter(key => key.isActive)?.length === 0 ? (
        <p className="text-sm text-gray-500">
          <span className="font-semibold">{org?.name}</span> currently doesnot have any active API keys.
          Click <span className="text-accent underline font-semibold cursor-pointer" onClick={() => handleCreate()}>here</span>{" "}
          to create one.
        </p>
      ) : null}
      <div className="space-y-4">
        {apiKeys.map((apiKey) => apiKey.isActive && (
          <div
            key={apiKey.id}
            className="group relative border border-gray-200 rounded-lg px-3 py-3 bg-white shadow-sm w-[400px] flex items-center"
          >
            <span className={getApiKeyStatusClassName(apiKey.isActive)} >
              {apiKey.isActive ? "Active" : "Inactive"}
            </span>
            <div className="flex flex-col items-start justify-center w-full">
              <span className="text-xs font-mono text-gray-800 break-all">
                {apiKey.apiKey}
              </span>
              <div className="mt-1 text-xs text-gray-500">
                Created: {dayjs(apiKey.createdAt).format("MMM D, YYYY h:mm A")}
              </div>
            </div>
            <button
              onClick={() => handleCopy(apiKey.apiKey, apiKey.id)}
              className="ml-2 text-gray-400 hover:text-primary transition-opacity opacity-0 group-hover:opacity-100 text-sm"
              title="Copy API Key"
            >
              {copiedKeyId === apiKey.id ? "Copied!" : "Copy"}
            </button>
            <button
              onClick={() => handleDelete(apiKey.id)}
              className="ml-2 text-gray-400 hover:text-red-600 transition-opacity opacity-0 group-hover:opacity-100 text-sm"
              title="Delete API Key"
            >
              Delete
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
